import { nullSafeGet } from './DataAccessUtils';
import { WalkthroughHeatMapRange } from '../types/walkthrough_heat_map_ranges';

// `color` ships without type definitions, so it is required rather than imported.
const Color = require('color');

export interface WalkthroughHeatMapColorSet {
	// Solid fill for heat map cells and legend chips — the configured color itself.
	swatch: string;
	// Tinted background behind the score pill.
	background: string;
	// Foreground for the "NN%" score pill text, darkened enough to stay readable on `background`.
	text: string;
	// Foreground for text drawn directly on `swatch` (the heat map's day numbers).
	swatchText: string;
}

// Solid colors, so the contrast measured is the contrast drawn. Near-black is the app's primary text
// color (grey-10).
export const NEAR_BLACK_SWATCH_TEXT = '#262626';
export const WHITE_SWATCH_TEXT = '#ffffff';
// Day numbers are white whenever white reaches this contrast against the cell, even where
// near-black would contrast more. This is a deliberate design choice below WCAG AA (4.5:1): white
// numbers on the saturated bands read as solid, filled cells, and 2.25:1 is just low enough for
// the default green (#22c55e, 2.28:1) to take white text.
export const MIN_WHITE_SWATCH_TEXT_CONTRAST = 2.25;

// Picks the day-number color by measured contrast rather than by how dark the color looks. Where
// white falls short the cell is light (luminance above ~0.42), so near-black always has at least
// ~6.7:1 there.
export const getReadableSwatchTextColor = (background: string): string =>
	Color(background).contrast(Color(WHITE_SWATCH_TEXT)) >= MIN_WHITE_SWATCH_TEXT_CONTRAST
		? WHITE_SWATCH_TEXT
		: NEAR_BLACK_SWATCH_TEXT;

// Reproduces the thresholds that were hardcoded before this became configurable: under 50% red,
// 50% up to (but not including) 100% yellow, exactly 100% green. The hex values match the colors
// the heat map rendered before, so a survey with no ranges looks exactly as it did.
export const DEFAULT_WALKTHROUGH_HEAT_MAP_RANGES: WalkthroughHeatMapRange[] = [
	{ color: '#f87171', lowerRange: 0 },
	{ color: '#facc15', lowerRange: 50 },
	{ color: '#22c55e', lowerRange: 100 },
];

// Ranges only cover scored walkthroughs. A day (or walkthrough) with no usable score falls back to
// this fixed, non-configurable grey.
export const NO_WALKTHROUGH_SCORE_COLOR: WalkthroughHeatMapColorSet = {
	swatch: '#e5e7eb',
	background: '#f3f4f6',
	text: 'rgba(0,0,0,0.45)',
	swatchText: getReadableSwatchTextColor('#e5e7eb'),
};

export const NO_WALKTHROUGH_SCORE_LABEL = 'No score';

// There is deliberately no upper bound on the number of ranges — a customer building a fine-grained
// gradient is doing so on purpose. The editor self-limits anyway: starts are integers bounded by
// their neighbours, so a new range can only be added while some gap still has room for one.
//
// Two is a real floor though: with a single range every score is the same color, which makes the
// heat map meaningless.
export const MIN_WALKTHROUGH_HEAT_MAP_RANGES = 2;

const isUsableNumber = (value: any): boolean => typeof value === 'number' && Number.isFinite(value);

export const isValidRangeColor = (color: any): boolean => {
	if (typeof color !== 'string' || color.length === 0) {
		return false;
	}
	try {
		Color(color);
		return true;
	} catch (parseError) {
		return false;
	}
};

// Derives the pill tint and readable text color from whatever hex the customer picked, so an
// arbitrary color from the label picker still renders a legible score pill.
export const getWalkthroughHeatMapColorSet = (color: string): WalkthroughHeatMapColorSet => {
	if (!isValidRangeColor(color)) {
		return NO_WALKTHROUGH_SCORE_COLOR;
	}
	const parsedColor = Color(color);
	return {
		swatch: color,
		background: parsedColor.lightness(93).hex(),
		// Pale picks (pastels, yellows) would be invisible as text on that tint, so cap the lightness.
		text: parsedColor.lightness(Math.min(parsedColor.lightness(), 38)).hex(),
		swatchText: getReadableSwatchTextColor(color),
	};
};

// Score percentages arrive from the backend as two-decimal strings ("75.00"). Returns null for
// anything that is not a usable number so callers can distinguish "no score" from a genuine 0%.
export const parseScorePercentage = (value: any): number | null => {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const parsed = typeof value === 'number' ? value : parseFloat(value);
	return Number.isFinite(parsed) ? parsed : null;
};

// Sorts ranges ascending and pins the lowest to 0 so every percentage is covered by some color.
export const normalizeWalkthroughHeatMapRanges = (
	ranges: WalkthroughHeatMapRange[]
): WalkthroughHeatMapRange[] => {
	const sorted = [...ranges].sort((first, second) => first.lowerRange - second.lowerRange);
	return sorted.map((range, index) => ({
		color: range.color,
		lowerRange: index === 0 ? 0 : range.lowerRange,
	}));
};

// Returns the usable ranges out of a stored value, or null when there are none — absent, empty,
// malformed, or carrying an unparseable color. A bad value must never blank out the heat map.
const extractUsableRanges = (ranges: any): WalkthroughHeatMapRange[] | null => {
	if (!Array.isArray(ranges)) {
		return null;
	}
	const usableRanges = ranges
		.filter(
			(range) =>
				range && isValidRangeColor(range.color) && parseScorePercentage(range.lowerRange) !== null
		)
		.map((range) => ({
			color: range.color,
			lowerRange: parseScorePercentage(range.lowerRange),
		}));

	return usableRanges.length > 0 ? normalizeWalkthroughHeatMapRanges(usableRanges) : null;
};

export const getWalkthroughHeatMapRanges = (ranges: any): WalkthroughHeatMapRange[] =>
	extractUsableRanges(ranges) || DEFAULT_WALKTHROUGH_HEAT_MAP_RANGES;

// The backend already resolves template inheritance into the survey's own field
// (SiteSurveyServiceImpl.hydrateSiteSurveys), so there is nothing to fall through to here.
export const getWalkthroughHeatMapRangesForSiteSurvey = (
	siteSurvey: any
): WalkthroughHeatMapRange[] =>
	getWalkthroughHeatMapRanges(nullSafeGet('walkthroughHeatMapRanges', siteSurvey));

// A walkthrough carries its (already inheritance-resolved) site survey nested under `siteSurvey`.
export const getWalkthroughHeatMapRangesForWalkthrough = (
	walkthrough: any
): WalkthroughHeatMapRange[] =>
	getWalkthroughHeatMapRanges(nullSafeGet('siteSurvey.walkthroughHeatMapRanges', walkthrough));

// A percentage belongs to the highest range whose lowerRange it meets or exceeds, so a boundary
// value (50 with a 0/50/100 ladder) lands in the upper range.
export const getWalkthroughHeatMapRangeForPercentage = (
	percentage: number | null | undefined,
	ranges: WalkthroughHeatMapRange[]
): WalkthroughHeatMapRange | null => {
	if (!isUsableNumber(percentage) || !Array.isArray(ranges) || ranges.length === 0) {
		return null;
	}
	return ranges.reduce(
		(matched, range) => (percentage >= range.lowerRange ? range : matched),
		ranges[0]
	);
};

export const getWalkthroughHeatMapColorsForPercentage = (
	percentage: number | null | undefined,
	ranges: WalkthroughHeatMapRange[]
): WalkthroughHeatMapColorSet => {
	const range = getWalkthroughHeatMapRangeForPercentage(percentage, ranges);
	return range ? getWalkthroughHeatMapColorSet(range.color) : NO_WALKTHROUGH_SCORE_COLOR;
};

// "50% - 100%" — the range's own start through the start of the range above it. A range pinned at
// the top of the scale collapses to a single value ("100%") rather than reading "100% - 100%".
export const getWalkthroughHeatMapRangeLabel = (
	range: WalkthroughHeatMapRange,
	index: number,
	ranges: WalkthroughHeatMapRange[]
): string => {
	const upperBound = index === ranges.length - 1 ? 100 : ranges[index + 1].lowerRange;
	return range.lowerRange >= upperBound
		? `${upperBound}%`
		: `${range.lowerRange}% - ${upperBound}%`;
};
